// Trip API service
// This file contains API calls for trip management

import { getTokensFromCache } from '../../auth/utils/tokenCache';
import { extractTripUuid } from '../utils/idUtils';
import { filterTripRecords } from '../utils/tripUtils';

export interface TripDates {
  type: 'concrete' | 'flexible';
  startDate: string; // YYYY-MM-DD format required by backend
  endDate: string; // YYYY-MM-DD format required by backend
  startOffsetDaysPositive?: number;
  startOffsetDaysNegative?: number;
  endOffsetDaysPositive?: number;
  endOffsetDaysNegative?: number;
}

export interface TripTravelers {
  adults: number;
  children: number;
}

export interface Trip {
  id: string | number;
  title: string;
  destinations: string[];
  dates: TripDates;
  status: string;
  travelers?: TripTravelers;
  images?: string[];
  activities?: string[];
  budget?: string | number | { amount: number; currency: string };
  description?: string;
  location?: {
    lat: number;
    lng: number;
    address: string;
  };
  progress?: number;
  rating?: number;
  memories?: number;
  conversation_id?: string;
  /**
   * The trip's agent chat — sent only to the owner and the collaborator. Its
   * presence IS the permission to see the chat; a member never receives it.
   */
  chatConversationId?: string;
  /**
   * May this viewer WRITE to it? The owner only; a collaborator reads along.
   * Stated by the server rather than derived from `viewerRole` on each client,
   * and enforced again by the conversation Lambda on the write itself.
   */
  chatCanWrite?: boolean;
  // Reactions enrichment (T2.14/D-25): the server merges the public like count
  // and THIS viewer's own state onto every trip read — one request per screen.
  // Bookmarks are private, so there is deliberately no save count here.
  likes?: number;
  viewer?: { liked: boolean; saved: boolean };
  // T2.3 (D-35): the caller's relationship to this trip + its visibility.
  viewerRole?: 'owner' | 'collaborator' | 'member' | 'buyer' | 'follower-viewer' | 'public-viewer';
  visibility?: 'private' | 'followers' | 'public'; // explicit override only
  effectiveVisibility?: 'private' | 'followers' | 'public';
  budgetTier?: 'budget' | 'moderate' | 'premium'; // limited-DTO replacement for amounts
  // T2.5 (D-32): present on a CLONED trip → the source it was forked from. Its
  // presence locks the trip private + non-sellable and renders "Forked from …".
  forkOf?: { tripId: string; ownerId: string };
}

export type TripVisibilityChoice = 'default' | 'private' | 'followers' | 'public';

export interface TripMemberDto {
  userId: string;
  role: 'collaborator' | 'member';
  createdAt?: string;
}

export interface CreateTripRequest {
  title: string;
  destinations: string[];
  dates: TripDates;
  status: 'draft' | 'planning' | 'in_progress' | 'completed'; // Must match backend TripStatus enum
  travelMode?: 'drive' | 'walk' | 'transit'; // trip-level default for schedule recompute (T1.16)
  images?: string[];
  activities?: string[];
  budget?: {
    amount: number;
    currency: string;
  };
  travelers?: TripTravelers;
  description?: string;
  location?: {
    lat: number;
    lng: number;
    address: string;
  };
  conversation_id?: string;
}

export interface UpdateTripRequest extends Partial<CreateTripRequest> {
  id: string;
}

// UI-facing activity "view" shape. The trips server persists a richer contract
// ({ startTime, endTime, location: {type, name/place_id}, cost }); the adapter
// functions below translate at the API boundary so every screen keeps consuming
// this flat shape. `dateTime` = server startTime, `endDateTime` = server endTime.
// The travel leg INTO a stop from the previous one that day (T1.16). Computed
// server-side (Google, mode-aware, auto-walk short hops); `chosenMode` is the
// user's explicit per-leg mode when they've overridden it.
export interface ActivityTravelLeg {
  mode: string;             // 'drive' | 'walk' | 'transit'
  minutes: number | null;   // travel time; null = no route ("can't calculate")
  km: number | null;        // distance
  chosenMode?: string;
  crossDay?: boolean;       // advisory leg across a day boundary
  fromLabel?: string;       // origin stop's name (cross-day INbound leg: "from X")
  toLabel?: string;         // destination stop's name (end-of-day OUTbound leg: "to X")
  warning?: 'far' | 'detour'; // server route-sanity flag (very far / big detour off the corridor)
}

export interface Activity {
  id: string;
  tripId: string;
  title: string;
  description?: string;
  location: string;      // display name / address (server location.name)
  placeId?: string;      // Google place id (server location.place_id), when known
  dateTime?: string;     // ISO 8601 — server startTime
  endDateTime?: string;  // ISO 8601 — server endTime
  budget?: {
    amount: number;
    currency: string;
  };
  visitMinutes?: number;            // dwell time (T1.16)
  travelFromPrev?: ActivityTravelLeg; // inbound travel leg (T1.16)
  status: 'scheduled' | 'planned';
  createdAt?: string;
  updatedAt?: string;
}

export interface CreateActivityRequest {
  title: string;
  description?: string;
  location?: string;     // display name / address → server location {type:'text'}
  placeId?: string;      // Google place id → server location {type:'place_id'}
  dateTime?: string;     // ISO 8601 → server startTime
  endDateTime?: string;  // ISO 8601 → server endTime
  budget?: {
    amount: number;
    currency: string;
  };
}

export interface UpdateActivityRequest extends Partial<CreateActivityRequest> {
  id: string;
}

// ── Activity wire adapters (UI view ⇄ trips-server contract) ──────────────────

// Server activity record → UI Activity view. Tolerant of either contract so a
// stale response shape never blanks the screen.
const fromServerActivity = (raw: any): Activity => {
  const loc = raw?.location;
  const location = typeof loc === 'string' ? loc : (loc?.name || loc?.place_id || '');
  const placeId = loc && typeof loc === 'object' && loc.type === 'place_id' ? loc.place_id : undefined;
  const startTime = raw?.startTime || raw?.dateTime;
  return {
    id: raw?.id,
    tripId: raw?.tripId,
    title: raw?.title,
    description: raw?.description,
    location,
    placeId,
    dateTime: startTime,
    endDateTime: raw?.endTime,
    budget: raw?.cost || raw?.budget,
    visitMinutes: typeof raw?.visitMinutes === 'number' ? raw.visitMinutes : undefined,
    travelFromPrev: raw?.travelFromPrev || undefined,
    status: raw?.status || (startTime ? 'scheduled' : 'planned'),
    createdAt: raw?.createdAt,
    updatedAt: raw?.updatedAt,
  };
};

// UI create/update request → server activity body. Builds the structured
// location object and start/end times the server validates.
const toServerActivityBody = (req: Partial<CreateActivityRequest>): Record<string, any> => {
  const body: Record<string, any> = {};
  if (req.title !== undefined) body.title = req.title;
  if (req.description !== undefined) body.description = req.description;
  if (req.placeId) {
    body.location = { type: 'place_id', place_id: req.placeId, ...(req.location ? { name: req.location } : {}) };
  } else if (req.location) {
    body.location = { type: 'text', name: req.location };
  }
  if (req.dateTime) body.startTime = req.dateTime;
  // The server rejects endTime <= startTime; only send a strictly-later end.
  if (req.endDateTime && (!req.dateTime || new Date(req.endDateTime) > new Date(req.dateTime))) {
    body.endTime = req.endDateTime;
  }
  if (req.budget) body.cost = req.budget;
  return body;
};

export type TravelMode = 'drive' | 'walk' | 'transit';

const API_ENDPOINT = process.env.REACT_APP_APIGATEWAY_TRIPS_ENDPOINT;

// Get access token from cache using proper token cache utility
const getAccessToken = (): string | null => {
  try {
    const tokens = getTokensFromCache();
    return tokens?.accessToken || null;
  } catch {
    return null;
  }
};

export interface TripPage {
  trips: Trip[];
  /** Opaque; pass back as `cursor`. Absent when there are no more. */
  nextCursor?: string;
}

/**
 * ONE page of the caller's trips, newest first (10 by default, server-set).
 *
 * Trips shared WITH you arrive whole on the first page only — they come from a
 * different partition with its own ordering, so no single cursor could walk
 * both. See the server route for why.
 */
export const getTrips = async (cursor?: string): Promise<TripPage> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  const response = await fetch(`${API_ENDPOINT}/trips${query}`, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // No Bearer prefix - API Gateway uses Cognito authorizer
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ getTrips failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to fetch trips: ${response.statusText}`);
  }

  const data = await response.json();
  return { trips: filterTripRecords(data.trips || []), nextCursor: data.nextCursor };
};

/**
 * ONE page of SOMEONE ELSE'S trips, already filtered server-side to what the
 * caller may see (the same D-35 rule as opening a trip, resolved once for the
 * page). Backs the Trips count on a public profile.
 *
 * A block answers with an empty page rather than an error, so this never has
 * to distinguish "no trips" from "not allowed" — both render as zero, which is
 * the point: the relationship stays private.
 */
export const getUserTrips = async (userId: string, cursor?: string): Promise<TripPage> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();
  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  const query = cursor ? `?cursor=${encodeURIComponent(cursor)}` : '';
  const response = await fetch(
    `${API_ENDPOINT}/user/${encodeURIComponent(userId)}/trips${query}`,
    {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': accessToken,
      },
    },
  );

  if (!response.ok) {
    throw new Error(`Failed to fetch trips: ${response.statusText}`);
  }

  const data = await response.json();
  return { trips: filterTripRecords(data.trips || []), nextCursor: data.nextCursor };
};

/** Hard stop on the walk below — 50 pages is far past any real trip count. */
const MAX_TRIP_PAGES = 50;

/**
 * EVERY trip, by walking the cursor.
 *
 * For the callers that genuinely cannot work from a page: the calendar (a month
 * view missing older trips is silently wrong), the agent's context, and
 * lookup-by-title. Anything that renders a LIST should use `getTrips` with a
 * load-more instead — this is the expensive path and says so.
 *
 * The loop is the one the SERVER used to lack: before 2026-08-03 the trips
 * query had no cursor at all and simply stopped at DynamoDB's 1MB response,
 * dropping the rest with no error. Doing it here makes the cost visible and
 * bounded rather than invisible and truncating.
 */
export const getAllTrips = async (): Promise<Trip[]> => {
  const all: Trip[] = [];
  let cursor: string | undefined;

  for (let page = 0; page < MAX_TRIP_PAGES; page += 1) {
    const result = await getTrips(cursor);
    all.push(...result.trips);
    if (!result.nextCursor) return all;
    cursor = result.nextCursor;
  }

  console.warn(`[trips] stopped walking at ${MAX_TRIP_PAGES} pages — list may be incomplete`);
  return all;
};

export const getTripById = async (tripId: string | number): Promise<Trip> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId (UUID) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripId);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}`;
  console.log('🌐 getTripById API call:', { url, tripId: tripId });

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ getTripById failed:', { status: response.status, statusText: response.statusText, errorText, tripId });
    throw new Error(`Failed to fetch trip: ${response.statusText}`);
  }

  const data = await response.json();
  console.log('✅ getTripById response:', data);
  return data.trip;
};


// Small authorized-JSON helper for the roster/visibility endpoints.
const authorizedFetch = async (path: string, init?: RequestInit): Promise<any> => {
  if (!API_ENDPOINT) throw new Error('API Gateway endpoint not configured');
  const accessToken = getAccessToken();
  if (!accessToken) throw new Error('User not authenticated');
  const response = await fetch(`${API_ENDPOINT}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: accessToken, ...(init?.headers || {}) },
  });
  if (!response.ok) {
    const detail = await response.text().catch(() => '');
    const error: any = new Error(`Request failed (${response.status}) ${detail}`.trim());
    error.status = response.status;
    throw error;
  }
  const text = await response.text();
  return text ? JSON.parse(text) : {};
};

// ── T2.3 roster + visibility (share modal) ───────────────────────────────────

export const getTripMembers = async (tripId: string): Promise<{ ownerId: string; members: TripMemberDto[] }> => {
  const response = await authorizedFetch(`/trip/${encodeURIComponent(tripId)}/members`);
  return { ownerId: response.ownerId, members: response.members || [] };
};

export const addTripMember = async (
  tripId: string,
  userId: string,
  role: 'collaborator' | 'member',
): Promise<void> => {
  await authorizedFetch(`/trip/${encodeURIComponent(tripId)}/members`, {
    method: 'POST',
    body: JSON.stringify({ userId, role }),
  });
};

export const removeTripMember = async (tripId: string, userId: string): Promise<void> => {
  await authorizedFetch(`/trip/${encodeURIComponent(tripId)}/members/${encodeURIComponent(userId)}`, {
    method: 'DELETE',
  });
};

export const setTripVisibility = async (
  tripId: string,
  visibility: TripVisibilityChoice,
): Promise<void> => {
  await authorizedFetch(`/trip/${encodeURIComponent(tripId)}`, {
    method: 'PUT',
    body: JSON.stringify({ visibility }),
  });
};

// ── Discovery / Explore (BETA-3) ─────────────────────────────────────────────

/**
 * One PUBLIC trip as the discovery surface sees it. Deliberately thinner than
 * `Trip`: discovery shows what is public to everyone, so there is no budget,
 * no rating and no chat linkage here — and **no save count**, because bookmarks
 * are fully private (D-25). `liked`/`saved` are the VIEWER's own state.
 */
export interface DiscoverTrip {
  id: string;
  title: string;
  destinations: string[];
  ownerId: string;
  status?: string;
  dates?: { startDate?: string; endDate?: string };
  createdAt?: string;
  likes?: number;
  liked?: boolean;
  saved?: boolean;
}

export interface DiscoverPage {
  trips: DiscoverTrip[];
  nextCursor?: string;
}

/**
 * Browse or search public trips.
 *
 * `query` is OPTIONAL — omitted, this BROWSES (newest public trips), which is
 * what Explore opens with. With ~5 users the follow graph is empty, so browse
 * is the only way anyone finds anything.
 */
export const discoverTrips = async (
  query?: string,
  cursor?: string,
  limit?: number,
): Promise<DiscoverPage> => {
  const params = new URLSearchParams();
  if (query?.trim()) params.set('q', query.trim());
  if (cursor) params.set('cursor', cursor);
  if (limit) params.set('limit', String(limit));
  const suffix = params.toString();
  const data = await authorizedFetch(`/trips/search${suffix ? `?${suffix}` : ''}`);
  return { trips: data.trips || [], nextCursor: data.nextCursor };
};

// ── T2.5 clone / fork (D-32) ─────────────────────────────────────────────────

/**
 * Fork a READABLE trip into the caller's own drafts. The copy is a fresh draft,
 * locked private and non-sellable, stamped `forkOf` (renders "Forked from …");
 * photos do not travel. Returns the new trip so the caller can navigate to it.
 */
export const cloneTrip = async (
  tripId: string,
): Promise<{ trip: Trip; activityCount: number }> => {
  const response = await authorizedFetch(`/trip/${encodeURIComponent(tripId)}/clone`, {
    method: 'POST',
  });
  return { trip: response.trip, activityCount: response.activityCount ?? 0 };
};

// ── T2.6d trip photos ────────────────────────────────────────────────────────

/** Photos per trip and slots per presign call — both enforced server-side. */
export const MAX_TRIP_PHOTOS = 50;
export const MAX_TRIP_PHOTO_BATCH = 10;

export interface TripMediaView {
  mediaId: string;
  /** `pending` while the pipeline runs — no URL yet, so poll rather than render. */
  status: 'live' | 'pending' | string;
  /** Expiring full-size URL (REQ-82) — live items only. */
  url?: string;
  /** Expiring 256px thumbnail — live items only. */
  thumbUrl?: string;
  width?: number;
  height?: number;
  createdAt?: string;
}

/**
 * A trip's photos. The URLs are short-lived by design, so this is fetched with
 * the view and never cached — a stored URL stops working.
 */
export const getTripMedia = async (tripId: string): Promise<TripMediaView[]> => {
  const response = await authorizedFetch(`/trip/${encodeURIComponent(tripId)}/media`);
  return response.media || [];
};

export interface TripMediaUploadTarget {
  mediaId: string;
  upload: { url: string; fields: Record<string, string> };
}

/**
 * Presigned direct-to-S3 upload slots — ONE request mints up to `count`, so the
 * per-request costs (auth, access resolution, cap check) are paid once and the
 * uploads then go straight to S3 in parallel. Owner and collaborators only;
 * anyone else gets 403.
 */
export const presignTripMedia = async (
  tripId: string,
  count: number,
): Promise<TripMediaUploadTarget[]> => {
  const response = await authorizedFetch(`/trip/${encodeURIComponent(tripId)}/media/presign`, {
    method: 'POST',
    body: JSON.stringify({ count }),
  });
  return Array.isArray(response.media) ? response.media : [response.media];
};

export const createTrip = async (tripData: CreateTripRequest): Promise<Trip> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  const response = await fetch(`${API_ENDPOINT}/trip`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
    body: JSON.stringify(tripData),
  });

  if (!response.ok) {
    // Surface the server's validation message (e.g. "destinations must be an
    // array of strings") instead of the opaque HTTP status.
    const detail = await response.text().catch(() => '');
    let message: string = response.statusText;
    try {
      const parsed = JSON.parse(detail);
      message = parsed.message || parsed.error || message;
    } catch {
      if (detail) message = detail;
    }
    throw new Error(`Failed to create trip: ${message}`);
  }

  const data = await response.json();
  return data.trip;
};

export const updateTrip = async (tripData: UpdateTripRequest): Promise<Trip> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId (UUID) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripData.id);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}`;
  console.log('🌐 updateTrip API call:', { url, tripId: tripData.id });

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
    body: JSON.stringify(tripData),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ updateTrip failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to update trip: ${response.statusText}`);
  }

  const data = await response.json();
  return data.trip;
};

export const deleteTrip = async (tripId: string | number): Promise<void> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId (UUID) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripId);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}`;
  console.log('🌐 deleteTrip API call:', { url, tripId });

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ deleteTrip failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to delete trip: ${response.statusText}`);
  }

  console.log('✅ Trip deleted successfully');
};

// Activity API functions

export const getActivities = async (tripId: string): Promise<Activity[]> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId (UUID) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripId);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}/activities`;
  console.log('🌐 getActivities API call:', { url, tripId });

  const response = await fetch(url, {
    method: 'GET',
    headers: {
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ getActivities failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to fetch activities: ${response.statusText}`);
  }

  const data = await response.json();
  return (data.activities || []).map(fromServerActivity);
};

export const createActivity = async (tripId: string, activityData: CreateActivityRequest): Promise<Activity> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId (UUID) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripId);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}/activity`;
  // Translate to the server contract (structured location, start/end times);
  // the server derives status from whether times are present.
  const body = toServerActivityBody(activityData);
  console.log('🌐 createActivity API call:', { url, tripId, body });

  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ createActivity failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to create activity: ${response.statusText}`);
  }

  const data = await response.json();
  return fromServerActivity(data.activity);
};

export const updateActivity = async (tripId: string, activityId: string, activityData: Partial<CreateActivityRequest>): Promise<Activity> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  // URL-encode the tripId and activityId (UUIDs) for safe URL transmission
  const encodedTripId = encodeURIComponent(tripId);
  const encodedActivityId = encodeURIComponent(activityId);
  const url = `${API_ENDPOINT}/trip/${encodedTripId}/activity/${encodedActivityId}`;
  const body = toServerActivityBody(activityData);
  console.log('🌐 updateActivity API call:', { url, tripId, activityId, body });

  const response = await fetch(url, {
    method: 'PUT',
    headers: {
      'Content-Type': 'application/json',
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ updateActivity failed:', { status: response.status, statusText: response.statusText, errorText });
    throw new Error(`Failed to update activity: ${response.statusText}`);
  }

  const data = await response.json();
  return fromServerActivity(data.activity);
};

// Set the transport mode for a stop's INBOUND leg (T1.16). The server stamps
// travelFromPrev.chosenMode, refetches that leg from Google in the chosen mode,
// and re-times the day — so callers should reload the day's activities after.
export const setActivityTravelMode = async (
  tripId: string,
  activityId: string,
  mode: TravelMode,
): Promise<void> => {
  if (!API_ENDPOINT) throw new Error('API Gateway endpoint not configured');
  const accessToken = getAccessToken();
  if (!accessToken) throw new Error('User not authenticated');

  const url = `${API_ENDPOINT}/trip/${encodeURIComponent(tripId)}/activity/${encodeURIComponent(activityId)}`;
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': 'application/json', 'Authorization': accessToken },
    body: JSON.stringify({ travelMode: mode }),
  });
  if (!response.ok) {
    throw new Error(`Failed to set travel mode (${response.status})`);
  }
};

export const deleteActivity = async (tripId: string, activityId: string): Promise<void> => {
  if (!API_ENDPOINT) {
    throw new Error('API Gateway endpoint not configured');
  }

  const accessToken = getAccessToken();

  if (!accessToken) {
    throw new Error('User not authenticated');
  }

  const url = `${API_ENDPOINT}/trip/${encodeURIComponent(tripId)}/activity/${encodeURIComponent(activityId)}`;
  console.log('🌐 deleteActivity API call:', { url, tripId, activityId });

  const response = await fetch(url, {
    method: 'DELETE',
    headers: {
      'Authorization': accessToken, // API Gateway uses Cognito authorizer
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('❌ deleteActivity failed:', { status: response.status, errorText });
    throw new Error(`Failed to delete activity: ${response.statusText}`);
  }

  console.log('✅ Activity deleted successfully');
};

// ── Trip checklist (things to bring / sort out) ─────────────────────────────

/** Mirrors services/checklist.ts on the server, which is the authority. */
export const MAX_CHECKLIST_ITEMS = 30;

export interface ChecklistItem {
  tripId: string;
  itemId: string;
  title: string;
  description?: string;
  checked: boolean;
  createdAt: string;
  createdBy: string;
}

const checklistPath = (tripId: string | number) =>
  `/trip/${encodeURIComponent(extractTripUuid(tripId))}/checklist`;

export const getChecklist = async (tripId: string | number): Promise<ChecklistItem[]> => {
  const data: { items?: ChecklistItem[] } = await authorizedFetch(checklistPath(tripId));
  return data.items || [];
};

/**
 * Add one item or several in ONE request. The 30-item cap is checked
 * server-side against the whole batch, so splitting a batch into a call per
 * item could slip past the limit and leave a half-applied list behind.
 */
export const addChecklistItems = async (
  tripId: string | number,
  items: { title: string; description?: string }[],
): Promise<ChecklistItem[]> => {
  const data: { items?: ChecklistItem[] } = await authorizedFetch(checklistPath(tripId), {
    method: 'POST',
    body: JSON.stringify({ items }),
  });
  return data.items || [];
};

export const updateChecklistItem = async (
  tripId: string | number,
  itemId: string,
  changes: { title?: string; description?: string; checked?: boolean },
): Promise<ChecklistItem | null> => {
  const data: { item?: ChecklistItem } = await authorizedFetch(
    `${checklistPath(tripId)}/${encodeURIComponent(itemId)}`,
    { method: 'PUT', body: JSON.stringify(changes) },
  );
  return data.item || null;
};

export const deleteChecklistItem = async (
  tripId: string | number,
  itemId: string,
): Promise<void> => {
  await authorizedFetch(`${checklistPath(tripId)}/${encodeURIComponent(itemId)}`, {
    method: 'DELETE',
  });
};
